import React, { useEffect, useState } from 'react'
import FlexView from 'react-flexview/lib'
import { Grid } from '@material-ui/core'
import { Action } from '../../models/Action'
import operatorApi from '../../api/operators.api'
import SkywatchDropzone from '../Core/SkywatchDropzone'
import { DropzonePlaceHolder } from '../Core/DropzonePlaceHolder'
import { ReactComponent as ExpandIcon } from '../../assets/expand_icon.svg'
import { SkyInformationPopover } from '../Core/Popover/SkyPopover'
import { ReactComponent as PersonIcon } from '../../assets/person.svg'
import { ReactComponent as TrashIcon } from '../../assets/trash.svg'
import { ReactComponent as RedTrashIcon } from '../../assets/red_trash.svg'
import { ReactComponent as AlertCircle } from '../../assets/alert_circle_icon.svg'
import { ReactComponent as PurpleRepeatIcon } from '../../assets/repeat_purple_icon.svg'
import { awaitWithLoading } from '../../services/utils'
import { SessionContextStore } from '../../context/SessionContext'
import './MotorcycleEndorsementsManagement.scss'
import { MotorcycleEndorsementPopup } from './MotorcycleEndorsementPopup'
import { SkywatchDialog } from '../PopUps/SkywatchDialog'
import { UserApi } from '../../api/user.api'

const TOOLTIP_TEXT =
  'According to LA state law, customers must include their driver’s license copy as part of the insurance bind flow. Below are the images per driver that the customer uploaded for your review. If needed, replacing the image is available for admins only.'

export const MotorcycleEndorsementsManagement = () => {
  const sessionContext = React.useContext(SessionContextStore)
  const userInsuredPersons = sessionContext.user.userInsuredPersons
  const [openDeletePopup, setOpenDeletePopup] = useState(false)
  const [openReplacePopup, setOpenReplacePopup] = useState(false)
  const [currFile, setCurrFile] = useState(undefined)
  const [currImage, setCurrImage] = useState(undefined)
  const [currPerson, setCurrPerson] = useState({})

  const handleDelete = async person => {
    let updatedOperator = {
      ...person,
      driver_license_image_url: undefined,
    }
    updatedOperator.operates = updatedOperator.user_vehicle_details.map(q => q.id)
    let res = await awaitWithLoading(operatorApi.updateOperator(updatedOperator))
    if (res.ok) {
      sessionContext.dispatch(new Action('UPDATE_OPERATOR', { ...updatedOperator }))
    }
  }

  const handleOnFileSelected = async (person, file) => {
    let imageUrlRes = await operatorApi.uploadLicense(person.id, file)

    let updatedOperator = {
      ...person,
      driver_license_image_url: imageUrlRes.data?.image_url,
    }
    updatedOperator.operates = updatedOperator.user_vehicle_details.map(q => q.id)
    let res = await operatorApi.updateOperator(updatedOperator)
    if (res.ok) {
      sessionContext.dispatch(new Action('UPDATE_OPERATOR', { ...updatedOperator }))
    }
  }

  const MotorcycleEndorsement = ({ person }) => {
    const [error, setError] = useState()
    const [imageUrl, setImageUrl] = useState('')

    useEffect(() => {
      const getUrl = async () => {
        const res = await UserApi.getImageUrl(person.driver_license_image_url)
        setImageUrl(res.parsedData)
      }
      getUrl()
    }, [])

    return (
      <Grid item xs={6}>
        <div className="person-headline">
          <PersonIcon />
          {person.first_name} {person.last_name}
        </div>
        <div className="flex flex-flow-col gap-25 width-full">
          <FlexView className="file-container h-72">
            {person.driver_license_image_url ? (
              <>
                <div className="file-div flex">
                  <img className="img " src={`${imageUrl}`}></img>
                  <div className="file-name ">
                    {person.first_name} {person.last_name} MC Endorsement
                  </div>
                </div>
                <div className="align-content-center trash gap-10 ">
                  <ExpandIcon className="clickable" onClick={() => setCurrImage(`${imageUrl}`)} />
                  <TrashIcon
                    className="clickable"
                    onClick={() => {
                      setCurrPerson(person)
                      setOpenDeletePopup(true)
                    }}
                  />
                </div>
              </>
            ) : (
              <div className="file-div flex">
                <AlertCircle />
                <div className="file-name">There is no motorcycle endorsement for this rider</div>
              </div>
            )}
          </FlexView>

          <div className="full-width">
            <SkywatchDropzone
              maxSize={5000000}
              onFileSelected={files => {
                if (files.length > 0) {
                  if (person.driver_license_image_url) {
                    setCurrFile(files[0])
                    setCurrPerson(person)
                    setOpenReplacePopup(true)
                  } else {
                    awaitWithLoading(handleOnFileSelected(person, files[0]))
                    setError(undefined)
                  }
                }
                if (files.length === 0) {
                  setError('Please select smaller than 5 MB image ')
                }
              }}
              accept="image/png, image/jpeg, image/gif"
              placeholder={<DropzonePlaceHolder mainText={'Upload on behalf of the customer'} />}
            />
            {error && <span className="error">{error}</span>}
          </div>
        </div>
      </Grid>
    )
  }

  return (
    <>
      <div className="margin-top-basic bold margin">
        Driver's motorcycle endorsement
        <SkyInformationPopover style={{ fontSize: '0.9rem' }}>
          <div className="text-small">{TOOLTIP_TEXT}</div>
        </SkyInformationPopover>
      </div>
      <Grid container spacing={3} className="margin-top-basic">
        {userInsuredPersons &&
          userInsuredPersons.map(person => <MotorcycleEndorsement onFileSelected={handleOnFileSelected} key={person.id} person={person} />)}
      </Grid>
      <MotorcycleEndorsementPopup
        open={openDeletePopup}
        title={'Are you sure?'}
        body={'Click delete if you want to remove this file. If otherwise - just cancel.'}
        icon={<RedTrashIcon />}
        onCancel={() => {
          setOpenDeletePopup(false)
          setCurrPerson({})
        }}
        onConfirm={() => handleDelete(currPerson)}
        onConfirmButtonText="Delete"
      />
      <MotorcycleEndorsementPopup
        open={openReplacePopup}
        title={'Replace Driver License Image'}
        body={
          <>
            Are you sure you want to upload this image? <br />
            Once uploading another image, the current one will be deleted.
          </>
        }
        isPurple={true}
        icon={<PurpleRepeatIcon />}
        onCancel={() => {
          setOpenReplacePopup(false)
          setCurrPerson({})
          setCurrFile(undefined)
        }}
        onConfirm={() => awaitWithLoading(handleOnFileSelected(currPerson, currFile))}
        onConfirmButtonText="Replace"
      />
      <SkywatchDialog open={currImage} closeDialog={() => setCurrImage(undefined)} maxWidth={'xs'}>
        <FlexView column>
          <div className="text-body black margin-top-basic margin-bottom-basic">
            <img className="image-size" src={currImage}></img>
          </div>
          <div className="flex margin-bottom-basic gap-12">
            <button onClick={() => setCurrImage(undefined)} className="white-button width-half">
              Close
            </button>
          </div>
        </FlexView>
      </SkywatchDialog>
    </>
  )
}
